import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Folder as FolderIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import { useGetBackendUserStorageFoldersQuery } from "features/adminUsers/adminUsers";
import { AdminStorageFolderItem } from "types/interfaces/adminUsers";

const SEARCH_DEBOUNCE_MS = 300;

function parentPrefixOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? "" : path.slice(0, i);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (path: string) => void;
  /** Backend (auth.User) whose Data Storage is browsed. */
  userId: number;
  /** Path the job already points at: the picker opens there, however deep it is. */
  initialPath?: string;
}

/**
 * Browse a backend user's Data Storage and pick one folder as an upload-bot source.
 *
 * Navigation is free at every depth: the folder holding the employee folders is not
 * always the first level (``.../new_timecards/ON6`` sits two levels down), and no
 * reliable signal separates an employee folder from a grouping one - the folder
 * counts on each row are what tell the two apart by eye.
 */
export default function DataStorageFolderPickerDialog({
  open,
  onClose,
  onConfirm,
  userId,
  initialPath,
}: Props) {
  // "" is the roots screen: the user's Data Storage folders.
  const [browsePrefix, setBrowsePrefix] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Open where the job already points, at whatever depth, so editing a job does
  // not silently drop the deeper segments of its path.
  useEffect(() => {
    if (!open) return;
    setBrowsePrefix((initialPath || "").trim().replace(/\/+$/, ""));
    setSearchInput("");
    setSearch("");
  }, [open, initialPath]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isFetching, isError } = useGetBackendUserStorageFoldersQuery(
    { id: userId, prefix: browsePrefix || undefined, search: search || undefined },
    { skip: !open || !userId },
  );

  const items: AdminStorageFolderItem[] = useMemo(() => data?.items ?? [], [data]);
  const rootPrefix = data?.root_prefix ?? "";
  const rootLabel = data?.root_display_name || rootPrefix;

  // Crumbs are relative to the folder root - the users/<id>/<vendor> head is
  // platform bookkeeping and only crowds the trail.
  const relativeSegments = useMemo(() => {
    if (!browsePrefix || !rootPrefix) return [];
    if (browsePrefix === rootPrefix) return [];
    return browsePrefix.slice(rootPrefix.length + 1).split("/").filter(Boolean);
  }, [browsePrefix, rootPrefix]);

  const goTo = (prefix: string) => {
    setBrowsePrefix(prefix);
    setSearchInput("");
    setSearch("");
  };

  const atRoots = !browsePrefix;
  const showEmptyFolderHint =
    !atRoots && !isFetching && !isError && !search && items.length === 0;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Select source folder"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={atRoots}
            onClick={() => {
              if (!browsePrefix) return;
              onConfirm(browsePrefix);
              onClose();
            }}
          >
            Select this folder
          </Button>
        </>
      }
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        Open the Data Storage folder that holds the files you want to upload. Its
        subfolders are what the bot treats as employees.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <IconButton
          size="small"
          aria-label="Up one level"
          disabled={atRoots}
          onClick={() => goTo(browsePrefix === rootPrefix ? "" : parentPrefixOf(browsePrefix))}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Breadcrumbs
          separator="/"
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", overflow: "hidden" },
            "& .MuiBreadcrumbs-li": { minWidth: 0 },
          }}
        >
          <Link
            underline="hover"
            color={atRoots ? "text.primary" : "inherit"}
            onClick={(e) => {
              e.preventDefault();
              goTo("");
            }}
            sx={{ cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}
          >
            Data Storage
          </Link>
          {!atRoots && rootPrefix ? (
            <Link
              underline="hover"
              color={relativeSegments.length === 0 ? "text.primary" : "inherit"}
              onClick={(e) => {
                e.preventDefault();
                if (relativeSegments.length === 0) return;
                goTo(rootPrefix);
              }}
              sx={{
                cursor: relativeSegments.length === 0 ? "default" : "pointer",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 200,
              }}
            >
              {rootLabel}
            </Link>
          ) : null}
          {relativeSegments.map((seg, idx, arr) => {
            const prefix = [rootPrefix, ...arr.slice(0, idx + 1)].join("/");
            const isLast = idx === arr.length - 1;
            return (
              <Link
                key={prefix}
                underline="hover"
                color={isLast ? "text.primary" : "inherit"}
                onClick={(e) => {
                  e.preventDefault();
                  if (isLast) return;
                  goTo(prefix);
                }}
                sx={{
                  cursor: isLast ? "default" : "pointer",
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 160,
                }}
              >
                {seg}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search folders in this level…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <SearchIcon fontSize="small" sx={{ mr: 0.75, color: "text.disabled" }} />
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          minHeight: 220,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : isError ? (
          <Typography variant="body2" color="error" sx={{ p: 2 }}>
            Could not read this folder from storage. Try again, or pick another folder.
          </Typography>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {search
              ? "Nothing matches this search."
              : atRoots
                ? "No storage folders found for this user."
                : "No subfolders here."}
          </Typography>
        ) : (
          items.map((it) => (
            <Box
              key={it.prefix}
              onClick={() => goTo(it.prefix)}
              sx={{
                px: 2,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-of-type": { borderBottom: "none" },
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <FolderIcon fontSize="small" sx={{ color: "rgb(31,93,107)" }} />
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {it.display_name}
              </Typography>
              <FolderCounts item={it} />
            </Box>
          ))
        )}
      </Box>

      {data?.truncated ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
          Showing {items.length} of {data.total}. Use search to narrow it down.
        </Typography>
      ) : null}

      {showEmptyFolderHint ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
          This folder holds no subfolders, so an upload bot would find no employees in it.
        </Typography>
      ) : null}

      {!atRoots ? (
        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: "action.hover" }}>
          <Typography variant="caption" color="text.secondary">
            Selected path:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-all",
              fontFamily: "monospace",
              fontSize: "0.75rem",
              mt: 0.25,
            }}
          >
            {browsePrefix}
          </Typography>
        </Box>
      ) : null}
    </AppDialog>
  );
}

/** "12 folders - 30 files": the only cue for where the employee level starts. */
function FolderCounts({ item }: { item: AdminStorageFolderItem }) {
  const parts: string[] = [];
  if (item.subfolder_count != null) {
    parts.push(`${item.subfolder_count} ${item.subfolder_count === 1 ? "folder" : "folders"}`);
  }
  if (item.direct_file_count) {
    parts.push(`${item.direct_file_count} ${item.direct_file_count === 1 ? "file" : "files"}`);
  }
  if (parts.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
      {parts.join(" · ")}
    </Typography>
  );
}
